'use client'

import { useTranslation } from 'react-i18next'
import {
  Badge,
  Box,
  Button,
  Flex,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react'
import { CheckCircle2, Download, ShieldX, XCircle } from 'lucide-react'
import { useVerifyCertificateQuery } from '@/entities/certificate'
import { AuthLayout } from '@/pages/auth/ui/AuthLayout'
import type { AxiosError } from 'axios'

interface Props {
  token: string
}

export function VerifyCertificatePage({ token }: Props) {
  const { t } = useTranslation()
  const { data, isPending, isError, error } = useVerifyCertificateQuery(token)

  const axiosError = error as AxiosError | null
  const isNotFound = axiosError?.response?.status === 404
  const isRateLimited = axiosError?.response?.status === 429

  if (isPending) {
    return (
      <AuthLayout
        title={t('verify.title')}
        description={t('verify.description')}
      >
        <Flex justify="center" py={8}>
          <Spinner size="lg" color="primary.fg" />
        </Flex>
      </AuthLayout>
    )
  }

  if (isError) {
    return (
      <AuthLayout
        title={t('verify.title')}
        description={t('verify.description')}
      >
        <VStack gap={4} py={4}>
          <Box color="primary.fg">
            <ShieldX size={40} />
          </Box>
          <Text fontWeight="semibold" textAlign="center">
            {isRateLimited
              ? t('verify.rate_limited')
              : isNotFound
                ? t('verify.not_found')
                : t('verify.error')}
          </Text>
        </VStack>
      </AuthLayout>
    )
  }

  const isValid = data.status === 'VALID'
  const pdfUrl = data.pdf_download_url

  return (
    <AuthLayout
      title={t('verify.title')}
      description={t('verify.description')}
      maxW={isValid && pdfUrl ? '860px' : '420px'}
    >
      <VStack gap={5} align="stretch">
        <Flex justify="center">
          <Badge
            px={3}
            py={1}
            borderRadius="full"
            fontSize="sm"
            bg={isValid ? 'badge.green.20' : 'badge.red.20'}
            color={isValid ? 'badge.green.100' : 'badge.red.100'}
          >
            <Flex align="center" gap={1.5}>
              {isValid ? <CheckCircle2 size={14} /> : <XCircle size={14} />}
              {isValid ? t('verify.status_valid') : t('verify.status_revoked')}
            </Flex>
          </Badge>
        </Flex>

        {isValid && pdfUrl ? (
          <>
            <Box
              borderWidth="1px"
              borderColor="border"
              borderRadius="xl"
              overflow="hidden"
              bg="white"
            >
              <iframe
                src={pdfUrl}
                title={t('verify.pdf_preview')}
                style={{
                  width: '100%',
                  height: '70vh',
                  minHeight: '420px',
                  border: 'none',
                  display: 'block',
                }}
              />
            </Box>

            <Button
              asChild
              variant="outline"
              borderRadius="md"
              w="full"
              borderColor="border"
            >
              <a href={pdfUrl} target="_blank" rel="noopener noreferrer">
                <Flex align="center" justify="center" gap={2}>
                  <Download size={16} />
                  {t('verify.download_pdf')}
                </Flex>
              </a>
            </Button>
          </>
        ) : isValid ? (
          <Text fontSize="sm" color="fg.muted" textAlign="center">
            {t('verify.pdf_unavailable')}
          </Text>
        ) : (
          <VStack gap={2}>
            <Text fontSize="sm" color="fg.muted" textAlign="center">
              {t('verify.revoked_hint')}
            </Text>
            {data.certificate_number && (
              <Text fontSize="sm" fontWeight="semibold" textAlign="center">
                {t('verify.certificate_number')}: {data.certificate_number}
              </Text>
            )}
          </VStack>
        )}
      </VStack>
    </AuthLayout>
  )
}
